import React from 'react'
import { cn } from '@/lib/utils'

/**
 * TargetGauge3 (W8, Targets) — the v2 cockpit gauge, ported onto a FORCED dark
 * instrument panel (mkt3-panel-dark mkt3-panel-hot) floating inside a light section.
 * This is the page's hinge motif: a room of night dropped into daylight.
 *
 * Copy is quoted verbatim from docs/marketing/2026-07-05-landing3-copy.md §W8 and uses
 * ONLY the locked Target vocabulary (docs/target-vocabulary.md): Pushing / On track /
 * Easing, Projected end-of-month, Target, ADR floor. The microline was de-mechanism'd
 * 2026-07-05 (founder direction) to "ADR floor respected · safeguards active" — its old
 * engine-internal tail is retired (see the copy doc's §W8 and claims-register §3).
 * 58.4% projected vs 62.0% target are illustrative fiction. The two readouts count up
 * ONCE on reveal; prefers-reduced-motion (and no-matchMedia/no-rAF environments) render
 * the final values statically.
 */

// One-shot count-up that respects prefers-reduced-motion and degrades gracefully where
// requestAnimationFrame is unavailable (e.g. jsdom) — landing straight on the target.
function useCountUp(target: number, live: boolean, decimals = 1) {
  const [value, setValue] = React.useState(target)

  React.useEffect(() => {
    if (!live) return
    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof requestAnimationFrame === 'undefined') {
      setValue(target)
      return
    }
    setValue(0)
    let raf = 0
    const start = performance.now()
    const duration = 1100
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3) // easeOutCubic
      setValue(target * eased)
      if (t < 1) raf = requestAnimationFrame(tick)
      else setValue(target)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, live])

  return value.toFixed(decimals)
}

export default function TargetGauge3({ className }: { className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [live, setLive] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setLive(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLive(true)
          io.disconnect()
        }
      },
      { threshold: 0.35 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const projected = useCountUp(58.4, live)
  const targetVal = useCountUp(62.0, live)

  return (
    <div
      ref={ref}
      role="img"
      aria-label="A target gauge for July occupancy — projected end-of-month 58.4 per cent against a target of 62 per cent, in the Pushing state, with the ADR floor respected and safeguards active."
      className={cn('mkt3-panel-dark mkt3-panel-hot p-5 sm:p-6', className)}
    >
      <div className="flex items-center justify-between border-b border-white/[0.07] pb-4">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-300">
          July · occupancy target
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-400/40 bg-teal-400/10 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-300">
          <span aria-hidden="true" className="mkt3-live-dot h-1 w-1 rounded-full bg-teal-300" />
          Pushing
        </span>
      </div>

      <dl className="mt-5 space-y-4">
        <div className="flex items-baseline justify-between gap-4">
          <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">Projected end-of-month</dt>
          <dd className="mkt3-digits mkt3-digits-glow text-3xl font-bold tabular-nums sm:text-4xl">{projected}%</dd>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">Target</dt>
          <dd className="font-mono text-2xl font-semibold tabular-nums text-slate-200 sm:text-3xl">{targetVal}%</dd>
        </div>
      </dl>

      <div className="mt-6 grid grid-cols-3 gap-1 rounded-lg border border-white/10 bg-black/30 p-1 text-center font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
        <span className="rounded-md bg-teal-400/15 py-2 text-teal-300 shadow-[inset_0_0_0_1px_rgba(45,212,191,0.4)]">
          Pushing
        </span>
        <span className="py-2 text-slate-600">On track</span>
        <span className="py-2 text-slate-600">Easing</span>
      </div>

      <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-[0.04em] text-slate-500">
        ADR floor respected · safeguards active
      </p>
    </div>
  )
}
